// The shared half of every entity reference (SEM-6329 / LINK_INVENTORY B).
//
// PersonLink was the first of these. The second (DepartmentLink) would have
// copied three rules verbatim: what an absent id means, what the affordance
// looks like, and that a link is never an authorization decision. A copied rule
// can only drift, so the rules live here and each entity link decides ONE
// thing — the address.

import { Link as RouterLink } from "react-router";

export interface EntityLinkProps {
  /**
   * Absent id → the children render as plain text, never an empty href.
   * `undefined` is spelled out because exactOptionalPropertyTypes makes an
   * omitted prop and an explicitly-undefined one different types, and callers
   * pass fields that are legitimately undefined.
   */
  readonly entityId: string | null | undefined;
  /** Built only when the id is present, so a caller never composes a dead path. */
  readonly toPath: (id: string) => string;
  readonly children: React.ReactNode;
  readonly className?: string;
}

// Prose colour at rest, link affordance on hover/focus: these names sit inside
// dense tables where accent-colouring every one would read as a warning strip.
// Underline (not colour alone) carries the affordance, per spec 43.
export const ENTITY_LINK_CLASS =
  "rounded-sm hover:text-[var(--accent)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]";

export function EntityLink({
  entityId,
  toPath,
  children,
  className,
}: EntityLinkProps): React.ReactElement {
  if (entityId === undefined || entityId === null || entityId === "") {
    return <>{children}</>;
  }
  return (
    <RouterLink
      to={toPath(entityId)}
      className={
        className === undefined ? ENTITY_LINK_CLASS : `${ENTITY_LINK_CLASS} ${className}`
      }
    >
      {children}
    </RouterLink>
  );
}
